import React from 'react';
import { Controller } from 'react-hook-form';
import { useAuthenticateEntityMutation } from '../../api/useAuthenticateEntityMutation';
import { AuthenticateFidelityEntityLoading } from './AuthenticateFidelityEntity.loading';
import { useDocumentVaultContext } from '../../context/useDocumentVaultContext';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { Grid } from '../../../CommonComponents/OPL/Grid/Grid';
import { Row } from '../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../CommonComponents/OPL/Grid/Column';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { InputMask } from '../../../CommonComponents/OPL/Input/InputMask';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { useAuthenticateFidelityEntity } from './useAuthenticateFidelityEntity';

export const AuthenticateFidelityEntity = () => {
  const { isLoading } = useAuthenticateEntityMutation();

  if (isLoading) {
    return <AuthenticateFidelityEntityLoading />;
  }

  return <AuthenticationForm />;
};

const AuthenticationForm = () => {
  const { data, actions } = useAuthenticateFidelityEntity();

  const { setSelectedEntity, selectedEntity } = useDocumentVaultContext();

  const { formBag, isError, isLoading } = data;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = formBag;

  const { onAuthenticateEntity } = actions;

  const onRequestClose = () => {
    if (selectedEntity?.forceUserAuthentication) {
      setSelectedEntity({ ...selectedEntity, forceUserAuthentication: false });
    }

    setSelectedEntity(null);
  };

  return (
    <Modal
      contentLabel="Login Details"
      size="medium"
      isOpen
      shouldCloseOnOverlayClick={false}
      onRequestClose={onRequestClose}
    >
      <Box as="form" onSubmit={handleSubmit(onAuthenticateEntity)}>
        <ModalHeader>Login Details</ModalHeader>
        <ModalCloseButton onClick={onRequestClose} />

        {isError && (
          <Alert
            title="Login Failed"
            message="Unable to login to Fidelity. Please double check your information and try again."
            variant="error"
            mx="24"
          />
        )}

        <ModalBody>
          <Grid>
            <Row my="16">
              <Column width={6}>
                <Input
                  {...register('firstName')}
                  label="First Name"
                  type="text"
                  formVariant={errors.firstName ? 'error' : undefined}
                  helperText={errors.firstName?.message}
                  autoComplete="given-name"
                />
              </Column>

              <Column width={6}>
                <Input
                  {...register('lastName')}
                  label="Last Name"
                  type="text"
                  formVariant={errors.lastName ? 'error' : undefined}
                  helperText={errors.lastName?.message}
                  autoComplete="family-name"
                />
              </Column>
            </Row>

            <Row my="16">
              <Column width={6}>
                <Controller
                  control={control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      label="Date of Birth"
                      value={field.value}
                      mask="99/99/9999"
                      maskPlaceholder={null}
                      placeholder="MM/DD/YYYY"
                      formVariant={errors.dateOfBirth ? 'error' : undefined}
                      helperText={errors.dateOfBirth?.message}
                      autoComplete="bday"
                    />
                  )}
                />
              </Column>

              <Column width={6}>
                <Controller
                  control={control}
                  name="lastFourDigitsSSN"
                  render={({ field }) => (
                    <InputMask
                      {...field}
                      label="Last 4 of Social Security Number"
                      value={field.value}
                      mask="9999"
                      maskPlaceholder={null}
                      placeholder="****"
                      type="password"
                      formVariant={errors.lastFourDigitsSSN ? 'error' : undefined}
                      helperText={errors.lastFourDigitsSSN?.message}
                    />
                  )}
                />
              </Column>
            </Row>
          </Grid>
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onRequestClose}>
            Cancel
          </Button>

          <Button type="submit" ml="12" isLoading={isLoading}>
            Log in
          </Button>
        </ModalFooter>
      </Box>
    </Modal>
  );
};
